import env from '@beam-australia/react-env'
import { Upload } from '@mui/icons-material'
import moment from 'moment'
import { FC, useState } from 'react'
import { Accept, useDropzone } from 'react-dropzone'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { Box, Button, CircularProgress, Grid, Typography } from 'src/components/UI'
import Icon from 'src/components/UI/CustomUI/atoms/Icon'
import Autocomplete from 'src/components/UI/CustomUI/molecules/Autocomplete'
import Modal from 'src/components/UI/CustomUI/molecules/Modal'
import { filesActions, jobActions } from 'src/ducks/actions'
import { getEmployeeId } from 'src/ducks/selectors'
import {
  DOCUMENT_TYPE_OPTIONS,
  imageDocExcelTypes,
  MAX_FILES,
  ParseFilesUrl,
} from 'src/helpers'
import { UpdateRequest } from '~api/types'
import styles from './styles.module.scss'

interface PendingFile {
  name: string
  fileUrl: string
  description: string
  uploadedOn: number
  uploadedBy: string
}

interface UploadDocumentsModalProps {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  handleClose: () => void
}

const UploadDocumentsModal: FC<UploadDocumentsModalProps> = ({
  open,
  setOpen,
  handleClose,
}) => {
  const [docType, setDocType] = useState<any>(null)
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])
  const [uploadingFile, setUploadingFile] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const userId = useSelector(getEmployeeId)

  const FILES_URL = env('FILES_URL') ?? ''

  const dispatch = useDispatch()

  const resetForm = () => {
    setPendingFiles([])
    setDocType(null)
    setUploadingFile(false)
    setSubmitting(false)
  }

  const handleDropFiles = (droppedFiles: File[]) => {
    if (!droppedFiles?.length || uploadingFile || submitting) return

    const remaining = MAX_FILES - pendingFiles.length
    if (remaining <= 0) {
      toast.error(
        `Maximum ${MAX_FILES} files reached. Remove some files to add more.`
      )
      return
    }
    if (droppedFiles.length > remaining) {
      toast.error(
        `Too many files. You can add up to ${remaining} more (max ${MAX_FILES} total).`
      )
      return
    }

    const fileArray = Array.from(droppedFiles).slice(0, remaining)

    setUploadingFile(true)
    dispatch(
      filesActions.uploadFiles(fileArray, (succ, fileNameList) => {
        if (
          succ &&
          fileNameList &&
          fileNameList.length === fileArray.length
        ) {
          const newEntries: PendingFile[] = fileArray.map((f, idx) => ({
            name: f.name,
            fileUrl: ParseFilesUrl({
              baseUrl: FILES_URL,
              avatar: fileNameList[idx] ?? '',
            }),
            description: f.name ?? '',
            uploadedOn: moment().unix(),
            uploadedBy: userId,
          }))
          setPendingFiles((prev) => [...prev, ...newEntries])
        } else {
          toast.error(
            'One or more files failed to upload. Please try again.'
          )
        }
        setUploadingFile(false)
      })
    )
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDropFiles,
    multiple: true,
    accept: imageDocExcelTypes as unknown as Accept,
    disabled: uploadingFile || submitting,
  })

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handleUploadDocument = () => {
    if (!docType || pendingFiles.length === 0 || submitting) return

    const ops: UpdateRequest = pendingFiles.map((pf) => ({
      op: 'add',
      path: '/properties/files/-',
      value: {
        ...pf,
        fileType: docType?.key,
      },
    }))

    setSubmitting(true)
    dispatch(
      jobActions.updateJobByPath(ops, (succ) => {
        if (succ) {
          resetForm()
          handleClose()
        } else {
          setSubmitting(false)
        }
      })
    )
  }

  const onModalClose = () => {
    handleClose()
    resetForm()
  }

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Upload Document"
      onClose={onModalClose}
    >
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <Autocomplete
            label="Doc Type"
            placeholder="Select Type"
            options={DOCUMENT_TYPE_OPTIONS}
            value={docType}
            onChange={(v) => setDocType(v)}
            className={styles.fit_height}
          />
        </Grid>
        <Grid item>
          <Box
            {...getRootProps()}
            sx={{
              border: '1px dashed #1A2A55',
              minHeight: '64px',
              borderRadius: '4px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              background: isDragActive ? '#E8ECF5' : '#F0F3FA',
              cursor: uploadingFile || submitting ? 'default' : 'pointer',
              opacity: uploadingFile || submitting ? 0.85 : 1,
              flexDirection: 'column',
              gap: 1,
              py: 1,
            }}
          >
            <input {...getInputProps()} />
            {uploadingFile ? (
              <CircularProgress size={32} />
            ) : (
              <>
                <Typography margin="12px" align="center">
                  Click or drag files here — you can select multiple at once
                </Typography>
                <Icon name="Upload" />
              </>
            )}
          </Box>
        </Grid>
        {pendingFiles.length > 0 && (
          <Grid item container direction="column" spacing={1}>
            {pendingFiles.map((pf, index) => (
              <Grid item key={`${pf.fileUrl}-${index}`}>
                <Box
                  sx={{
                    display: 'flex',
                    padding: '12px',
                    borderRadius: '8px',
                    gap: '8px',
                    alignItems: 'center',
                    backgroundColor: '#F9FAFA',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box
                    display="flex"
                    gap={1}
                    alignItems="center"
                    sx={{ minWidth: 0, flex: 1 }}
                  >
                    <Icon name="Description" />
                    <Typography variant="body1" noWrap title={pf.name}>
                      {pf.name}
                    </Typography>
                  </Box>
                  <Button
                    onClick={() => removePendingFile(index)}
                    disabled={uploadingFile || submitting}
                    sx={{ padding: '0 !important', flexShrink: 0 }}
                  >
                    <Icon name="Delete" color="var(--red600)" />
                  </Button>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
        <Grid item container spacing={2}>
          <Grid item xs={6}>
            <Button
              variant="contained"
              sx={{
                height: '40px',
                backgroundColor: '#FAFBFF',
                color: '#3A66FF',
                border: 0,
                '&:hover': {
                  color: 'white',
                },
              }}
              fullWidth
              onClick={onModalClose}
              disabled={submitting}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              onClick={handleUploadDocument}
              variant="contained"
              color="primary"
              sx={{
                height: '40px',
              }}
              fullWidth
              endIcon={
                submitting ? undefined : (
                  <Upload
                    style={{
                      width: '12px',
                      height: '12px',
                      padding: '0px !important',
                    }}
                  />
                )
              }
              disabled={
                pendingFiles.length === 0 ||
                uploadingFile ||
                !docType ||
                submitting
              }
            >
              {submitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Upload'
              )}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Modal>
  )
}

export default UploadDocumentsModal
